import React, {
  FC, useEffect, useMemo, useState,
} from 'react';
import {
  IntegratedGrouping, GroupingState, SummaryState,
  IntegratedSummary, IntegratedSorting, Sorting, SortingState, FilteringState, IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import {
  Grid, TableHeaderRow, TableGroupRow, TableSummaryRow, TableColumnVisibility, ColumnChooser, Toolbar,
  TableColumnResizing, DragDropProvider, TableColumnReordering, VirtualTable, TableFilterRow,
} from '@devexpress/dx-react-grid-material-ui';
import { compareDate } from '@/utils/date';
import { getItem, saveItem } from '@/utils';
import { STORAGE_KEY_REPORT_EXPAND_ROW } from '@/constants/storage';
import { IGroupedTable } from '../../types';

const GroupedTable: FC<IGroupedTable> = ({
  rows, columns, isActiveExpand, isGroup, columnWidths, handleColumnWidthsChange, columnOrder,
  setColumnOrder, TotalCellComponent, hiddenColumns, handleHiddenColumns, isColumnsFilter = false,
  handleCellComponent, HandleRowComponent, group, subgroup, grouping,
  HeaderCellComponent, SummaryItems, groupSummaryItems, defaultSort,
  handleContentComponent, handleSummaryItemComponent, nameTable,
}) => {
  const [sorting, setSorting] = useState<Sorting[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const storageExpanded = JSON.parse(getItem(STORAGE_KEY_REPORT_EXPAND_ROW, false) || 'null') || [];

  const integratedSortingColumnExtensions = useMemo(() => {
    const sortingColumns = [];
    const columnTypesDates = ['date', 'month'];
    if (columnTypesDates.includes(group)) {
      sortingColumns.push({ columnName: 'grouped', compare: compareDate });
    }
    if (columnTypesDates.includes(subgroup)) {
      sortingColumns.push({ columnName: 'subgrouped', compare: compareDate });
    }
    return sortingColumns;
  }, [group, subgroup]);

  useEffect(() => {
    setSorting(defaultSort);
  }, [defaultSort, isGroup]);

  useEffect(() => {
    const expandedGroup = isActiveExpand ? rows.map((item) => String(item.grouped)) : [];
    const uniqueExpandedGroup = [...new Set(expandedGroup)];

    if (uniqueExpandedGroup) {
      if (isActiveExpand && ['reports'].includes(nameTable)) {
        const configTable = {
          ...JSON.parse(getItem(STORAGE_KEY_REPORT_EXPAND_ROW, false) || 'null'),
          [nameTable]: uniqueExpandedGroup,
        };

        saveItem(STORAGE_KEY_REPORT_EXPAND_ROW, JSON.stringify(configTable), false);
      }
      setExpanded(uniqueExpandedGroup);
    }
  }, [isActiveExpand, nameTable, rows]);

  const setExpandedGroups = (value: string[]) => {
    const uniqueExpList = [...new Set(value)];
    if (nameTable) {
      const configTable = {
        ...JSON.parse(getItem(STORAGE_KEY_REPORT_EXPAND_ROW, false) || 'null'),
        [nameTable]: uniqueExpList,
      };
      saveItem(STORAGE_KEY_REPORT_EXPAND_ROW, JSON.stringify(configTable), false);
    }

    setExpanded(value);
  };

  return (
    <>
      {
        isGroup && (
          <Grid rows={rows} columns={columns}>
            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
            <GroupingState
              grouping={grouping}
              expandedGroups={storageExpanded[nameTable] || expanded}
              onExpandedGroupsChange={setExpandedGroups}
            />
            <SummaryState groupItems={groupSummaryItems} totalItems={SummaryItems} />
            <IntegratedGrouping />
            <IntegratedSummary />
            <FilteringState defaultFilters={[]} />
            <IntegratedFiltering />
            <DragDropProvider />
            <VirtualTable rowComponent={HandleRowComponent} cellComponent={handleCellComponent} height="auto" />
            <TableColumnResizing
              resizingMode="widget"
              columnWidths={columnWidths}
              onColumnWidthsChange={handleColumnWidthsChange}
            />
            <TableColumnReordering order={columnOrder} onOrderChange={setColumnOrder} />
            <TableHeaderRow cellComponent={HeaderCellComponent} showSortingControls />
            {
              isColumnsFilter && (<TableFilterRow />)
            }
            <TableSummaryRow totalCellComponent={TotalCellComponent} />
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumns}
              onHiddenColumnNamesChange={handleHiddenColumns}
              columnExtensions={[{ columnName: 'grouped', togglingEnabled: false }]}
            />
            <Toolbar />
            <ColumnChooser />
            <TableGroupRow
              showColumnsWhenGrouped
              contentComponent={handleContentComponent}
              summaryItemComponent={handleSummaryItemComponent}
            />
          </Grid>
        )
      }
      {
        !isGroup && (
          <Grid rows={rows} columns={columns}>
            <SortingState sorting={sorting} onSortingChange={setSorting} />
            <IntegratedSorting columnExtensions={integratedSortingColumnExtensions} />
            <SummaryState totalItems={SummaryItems} />
            <IntegratedSummary />
            <FilteringState defaultFilters={[]} />
            <IntegratedFiltering />
            <DragDropProvider />
            <VirtualTable rowComponent={HandleRowComponent} cellComponent={handleCellComponent} height="auto" />
            <TableColumnResizing
              resizingMode="widget"
              columnWidths={columnWidths}
              onColumnWidthsChange={handleColumnWidthsChange}
            />
            <TableColumnReordering order={columnOrder} onOrderChange={setColumnOrder} />
            <TableHeaderRow cellComponent={HeaderCellComponent} showSortingControls />
            {
              isColumnsFilter && (<TableFilterRow />)
            }
            <TableSummaryRow totalCellComponent={TotalCellComponent} />
            <TableColumnVisibility
              hiddenColumnNames={hiddenColumns}
              onHiddenColumnNamesChange={handleHiddenColumns}
              columnExtensions={[{ columnName: 'grouped', togglingEnabled: false }]}
            />
            <Toolbar />
            <ColumnChooser />
          </Grid>
        )
      }
    </>
  );
};

export default GroupedTable;
